// Drosselung des automatischen Bestell-Abrufs beim Öffnen des Reiters „Bestellungen".
//
// Betreiber-Wunsch 2026-08-28: Wer den Reiter aufmacht, will die AKTUELLEN Bestellungen sehen,
// nicht den Stand des letzten Abrufs. Der Reiter selbst kann das nicht von sich aus wissen —
// VARIO baut das iframe bei jedem Reiterwechsel neu auf, das Modul startet also jedes Mal bei
// null ([[vario-tab-switch-unmounts-iframe-no-beforeunload]]).
//
// Ungedrosselt hiesse das: jeder Klick auf den Reiter stösst je Shop einen
// `gambio.refresh_orders`-Job an. Wer zwischen Bestellungen und Einstellungen hin- und herspringt,
// erzeugt so eine Job-Lawine gegen den Shop. Deshalb der Mindestabstand: innerhalb dieser Frist
// ist ohnehin nichts Neues zu holen, und die Liste steht ja bereits aus der Datenbank.
//
// Der Zeitstempel liegt in `sessionStorage` (überlebt den iframe-Austausch, gilt pro Browser-Tab
// und pro Benutzer, wird beim Schliessen des Tabs verworfen).

export const ORDERS_AUTO_REFRESH_KEY = 'gambio:ordersAutoRefreshAt';

/** Mindestabstand zwischen zwei automatischen Abrufen. Der Knopf „Aktualisieren" ignoriert ihn. */
export const ORDERS_AUTO_REFRESH_MIN_GAP_MS = 60_000;

/**
 * Ist seit dem letzten automatischen Abruf genug Zeit vergangen?
 *
 * `lastRunRaw` ist der rohe Wert aus dem Speicher (String, oder `null`, wenn noch nie gelaufen).
 * Ein unlesbarer Wert gilt als „noch nie gelaufen" — lieber einmal zu viel abrufen als einen
 * kaputten Zeitstempel dauerhaft als Sperre stehen zu lassen.
 */
export function shouldAutoRefreshOrders(
  lastRunRaw: string | null,
  now: number,
  minGapMs: number = ORDERS_AUTO_REFRESH_MIN_GAP_MS,
): boolean {
  if (!lastRunRaw) return true;
  const last = Number(lastRunRaw);
  if (!Number.isFinite(last) || last <= 0) return true;
  // Zeitstempel aus der Zukunft (Uhr gestellt) ebenfalls als abgelaufen behandeln.
  if (last > now) return true;
  return now - last >= minGapMs;
}

/**
 * Text der „Stand"-Anzeige nach einem Abruf aus dem Shop.
 *
 * Zweck ist Sichtbarkeit: „nichts Neues" ist eine ANDERE Aussage als „nicht gelaufen", und genau
 * die konnte man vorher nicht unterscheiden — der Abruf lief still, und wenn der Shop nichts
 * Neues hatte, änderte sich in der Liste nichts (Betreiber-Befund 2026-09-02).
 */
export function ordersRefreshSummary(o: {
  /** Uhrzeit als bereits formatierter String (z. B. „14:23"). */
  time: string;
  /** Vom Job geschriebene Bestellungen (neu ODER geändert) über alle Shops. */
  upserted: number;
  /** Shops, deren Abruf fehlschlug oder nicht rechtzeitig fertig wurde. */
  failed: number;
  /** Abgeschlossene Abruf-Jobs. */
  jobs: number;
}): string {
  if (o.jobs === 0 && o.failed > 0) return `Abruf fehlgeschlagen · Stand ${o.time}`;
  const teil = o.upserted > 0
    ? `${o.upserted} ${o.upserted === 1 ? 'Bestellung' : 'Bestellungen'} aktualisiert`
    : 'nichts Neues';
  return o.failed > 0
    ? `Stand ${o.time} · ${teil} · ${o.failed} Shop(s) mit Fehler`
    : `Stand ${o.time} · ${teil}`;
}
